/**
 * The life of a duel, as a closed set of states and the reasons it can end.
 *
 * ## Why `proposed` and `waiting_for_opponent` are both here
 *
 * They answer different questions. `proposed` means a row exists and a code has
 * been minted; nobody is holding the room. `waiting_for_opponent` means the
 * challenger is CONNECTED and the code is live. The difference matters at
 * expiry: an unheld proposal costs nothing and can be swept on a schedule,
 * while a held room has a socket to close and a person to tell.
 *
 * Collapsing them would also make "the challenger closed the tab before anybody
 * joined" indistinguishable from "the challenge was never picked up", and those
 * are two different things to say to the player who tries the code.
 */

export const PVP_MATCH_STATES = [
  /** Created. A code exists; no seat is connected. */
  'proposed',
  /** The challenger holds the room and the code is live. */
  'waiting_for_opponent',
  /** Both seats are taken and both are connected. The first tick has not run. */
  'ready',
  /** The simulation is advancing. */
  'running',
  /** It ended with a result - including a draw. */
  'completed',
  /** It ended before it ran, by somebody's choice. */
  'cancelled',
  /** It ended before it ran, by the clock. */
  'expired',
] as const;

export type PvpMatchState = (typeof PVP_MATCH_STATES)[number];

/** The states from which nothing further happens. */
export const PVP_TERMINAL_STATES = ['completed', 'cancelled', 'expired'] as const;

export type PvpTerminalState = (typeof PVP_TERMINAL_STATES)[number];

export function isPvpTerminalState(state: PvpMatchState): state is PvpTerminalState {
  return (PVP_TERMINAL_STATES as readonly string[]).includes(state);
}

/**
 * Why a duel stopped.
 *
 * `service_failure` is the honest one and the reason this list is not shorter.
 * If the room itself falls over mid-fight there is no trustworthy result: the
 * server cannot say who was winning, and inventing a winner from the last frame
 * it happens to remember would be a guess wearing a result's clothes. So the
 * match completes with NO winner and says why. A duel that pays nothing can
 * afford that honesty; the escrow prompt inherits it as a refund path rather
 * than as a bug to discover later.
 */
export const PVP_END_REASONS = [
  /** A ship reached zero health. There is a winner. */
  'death',
  /** The clock ran out with both alive. A draw. */
  'time_limit',
  /** Both ships died on the same tick. A draw. */
  'mutual_destruction',
  /** A seat stayed disconnected past the grace window. The other seat wins. */
  'abandonment',
  /** The challenger withdrew before anybody accepted. */
  'cancelled_by_challenger',
  /** Nobody accepted before the proposal's deadline. */
  'expired_unaccepted',
  /** The room failed. No winner, deliberately. */
  'service_failure',
] as const;

export type PvpEndReason = (typeof PVP_END_REASONS)[number];

/** Which end reasons may name a winner at all. */
const REASONS_WITH_A_WINNER: readonly PvpEndReason[] = ['death', 'abandonment'];

export function endReasonMayHaveWinner(reason: PvpEndReason): boolean {
  return REASONS_WITH_A_WINNER.includes(reason);
}

/**
 * The two seats. Not "player 1 and 2" - a seat is a position in the room, and
 * which account sits in it is a separate fact stored on the row.
 */
export const PVP_SEATS = ['a', 'b'] as const;

export type PvpSeat = (typeof PVP_SEATS)[number];

export function otherSeat(seat: PvpSeat): PvpSeat {
  return seat === 'a' ? 'b' : 'a';
}

/**
 * Why an `abandonment` was an abandonment.
 *
 * ## Why the broad reason was not enough
 *
 * `abandonment` unions two events that feel nothing alike to the person they
 * happened to: somebody pressing a button, and somebody's connection failing
 * for longer than the grace window. The POLICY is deliberately one - both end
 * the match the same way, and splitting the reason would have split the
 * `pvp_matches` column, the audit vocabulary and every rule keyed on it.
 *
 * What was missing is an EXPLANATION, and the explanation is a separate fact.
 * Without it the client had nothing to read but its own local state, and a
 * player who had just chosen to leave was told "Você não voltou dentro da
 * janela de reconexão" - not a shade of meaning, a falsehood.
 *
 * Two values, and not three: there is no "kicked", no "timed out by the server"
 * and no "unknown". An abandonment this build cannot explain carries no cause at
 * all, which is a different thing from a cause named `unknown` and reads as one.
 */
export const PVP_ABANDON_CAUSES = [
  /** The participant asked to leave, over their own socket. Immediate. */
  'voluntary',
  /** The participant stayed disconnected past the grace window. */
  'reconnect_timeout',
] as const;

export type PvpAbandonCause = (typeof PVP_ABANDON_CAUSES)[number];

/**
 * Who ended a duel by abandoning it, and how - in the identity a duel has.
 *
 * A SEAT, because that is what the two-seat wire, the `pvp_matches` row and
 * every persisted duel snapshot already speak. The team-shaped fact names a
 * participant instead; `legacy-duel.ts` is the one place the two are converted.
 */
export interface PvpDuelAbandonment {
  readonly cause: PvpAbandonCause;
  readonly seat: PvpSeat;
}

/**
 * The outcome as recorded. `winner: null` covers a draw AND a match that ended
 * without a trustworthy result - the two are told apart by `reason`, never by
 * the absence of a winner.
 */
export interface PvpResult {
  readonly winner: PvpSeat | null;
  readonly reason: PvpEndReason;
  /** Ticks the simulation actually advanced. Zero for a match that never ran. */
  readonly ticks: number;
  /**
   * Why an abandonment happened, or `null` for every other ending.
   *
   * OPTIONAL rather than required, and only here. This interface is a
   * PERSISTENCE shape - duel snapshots written by older builds are still in
   * Durable Object storage, and they carry no such key. Absent means "this
   * record cannot say", which is exactly what those records cannot. The running
   * simulation's own result type demands the field explicitly, so a new ending
   * can never forget to decide it.
   */
  readonly abandonment?: PvpDuelAbandonment | null;
}

/**
 * Why a connection was closed by the server.
 *
 * Distinct from {@link PvpEndReason}: a socket closing is not a match ending.
 * `superseded` is the two-tabs answer - the older socket is closed and the
 * match carries on, because the player is still there, just in a different tab.
 */
export const PVP_CLOSE_REASONS = [
  'superseded',
  'match_over',
  'unauthorised',
  'rate_limited',
  'malformed',
  'pvp_disabled',
] as const;

export type PvpCloseReason = (typeof PVP_CLOSE_REASONS)[number];
